import { useLayoutEffect, useRef, type ReactNode } from 'react';

import { useHeroRegistry } from './heroContext';

/**
 * Перенос `lib/src/presentation/widgets/maybe_hero.dart`.
 *
 * Логика та же: есть `tag` — оборачиваем в Hero, нет — отдаём ребёнка как
 * есть. Сам перелёт делается приёмом FLIP: новый виджет измеряет себя,
 * берёт из реестра прямоугольник предыдущего и едет из него в свой за
 * 250 мс — столько же длится переход между страницами (`FadePage`).
 *
 * Отличия от Dart: `createRectTween`, `flightShuttleBuilder`,
 * `placeholderBuilder` и `transitionOnUserGestures` не переносились — ни
 * один вызов их не задаёт, а без навигатора Flutter им нечему
 * соответствовать. Обёртка <div> в раскладке не бесплатна, поэтому её
 * можно донастроить через `className`.
 */

/** Длительность перелёта — как у перехода между страницами в `FadePage`. */
const FLIGHT_DURATION_MS = 250;

/** Запас, чтобы снять transform уже после конца перехода. */
const FLIGHT_CLEANUP_SLACK_MS = 50;

export interface MaybeHeroProps {
  readonly tag?: string | null;
  readonly className?: string;
  readonly children: ReactNode;
}

function startFlight(
  element: HTMLElement,
  from: DOMRect,
  to: DOMRect,
): (() => void) | undefined {
  if (from.width === 0 || from.height === 0) return undefined;
  if (to.width === 0 || to.height === 0) return undefined;

  // Стили ставятся через `setProperty`, а не присваиванием: элемент пришёл
  // параметром, и менять его поля на месте в этом проекте нельзя.
  const style = element.style;
  const clear = (): void => {
    style.removeProperty('transition');
    style.removeProperty('transform');
    style.removeProperty('transform-origin');
  };

  // Кадр 0: элемент стоит там, где был предыдущий герой.
  const shift = `translate(${from.left - to.left}px, ${from.top - to.top}px)`;
  const scale = `scale(${from.width / to.width}, ${from.height / to.height})`;
  style.setProperty('transform-origin', 'top left');
  style.setProperty('transition', 'none');
  style.setProperty('transform', `${shift} ${scale}`);

  // Кадр 1: отпускаем — дальше едет CSS.
  const frame = requestAnimationFrame(() => {
    style.setProperty('transition', `transform ${FLIGHT_DURATION_MS}ms ease-out`);
    style.setProperty('transform', 'none');
  });
  const timer = window.setTimeout(
    clear,
    FLIGHT_DURATION_MS + FLIGHT_CLEANUP_SLACK_MS,
  );

  return () => {
    cancelAnimationFrame(frame);
    window.clearTimeout(timer);
    clear();
  };
}

export function MaybeHero({ tag, className, children }: MaybeHeroProps) {
  const registry = useHeroRegistry();
  const elementRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const element = elementRef.current;
    if (tag == null || element === null || registry === null) return undefined;
    const from = registry.read(tag);
    const to = element.getBoundingClientRect();
    // Свой прямоугольник кладём в реестр до перелёта: он и станет точкой
    // старта для следующего героя с тем же тегом.
    registry.write(tag, to);
    if (from === null) return undefined;
    return startFlight(element, from, to);
  }, [tag, registry]);

  if (tag == null) return <>{children}</>;
  return (
    <div ref={elementRef} className={className}>
      {children}
    </div>
  );
}
